import { fetchGetExchangeableQuantity, fetchProductDetails, fetchActivityCountdownByProduct } from '@/services';
import { PlaceOrderType, ProductType, type AnyObj, type AnyObjOrNull } from '@/types';
import { businessUtils } from '@/utils';
import NP from 'number-precision';
import { defineStore } from 'pinia';
import { usePlaceOrderStore } from './placeOrder';

interface State {
  /** 商品详情数据 */
  commodityDetails: AnyObjOrNull;
  /** 商品购买类型 */
  purchaseType: PlaceOrderType;
  /** 订阅操作的相关记录 */
  subscribeOptions: {
    cycleIndex: number;
    IntervalIndex: number;
  };
  /** 是否订阅标识 */
  isSubscribe: boolean;
  /** 广告字段 */
  adKeys: {
    click_id: string;
    click_time: string;
  };
  exchangeableQuantity: number;
  /** 商品活动数据 */
  activityCountdownByProductData: AnyObjOrNull;
  /** 弹窗实例  */
  popupRef: {
    serviceDescription: AnyObject | null;
    promotionActivity: AnyObject | null;
  };
}

export const useCommodityStore = defineStore('commodity', {
  state: (): State => ({
    commodityDetails: null,
    purchaseType: PlaceOrderType.regularOrder,
    subscribeOptions: {
      cycleIndex: 0,
      IntervalIndex: 0,
    },
    isSubscribe: false,
    adKeys: {
      click_id: '',
      click_time: '',
    },
    exchangeableQuantity: -1,
    activityCountdownByProductData: null,
    popupRef: {
      serviceDescription: null,
      promotionActivity: null,
    },
  }),

  getters: {
    /** 是否会员商品 */
    isVipProduct(state) {
      return !!state.commodityDetails?.customFields.isVipProduct;
    },
    /** 是否包邮 */
    isFreeShipping(state) {
      return state.commodityDetails?.variants.some((it: AnyObj) => it.price === 1);
    },
    /** 是否加入购物车流程类型 */
    isShoppingTrolleyFlow(state) {
      return (
        state.purchaseType === PlaceOrderType.shoppingTrolley ||
        state.purchaseType === PlaceOrderType._regularOrderToShoppingTrolley
      );
    },
    /** 定时销售配置信息 */
    timedSaleConfig(state) {
      return businessUtils.commodity.getTimedSaleConfig(state.commodityDetails?.customFields);
    },
    /** 虚拟商品配置信息 */
    virtualConfig(state) {
      const customFields = state.commodityDetails?.customFields;
      return {
        is: customFields?.productType === ProductType.Virtual,
        type: customFields?.virtualTargetType,
      };
    },
    /** 积分兑换信息 */
    pointsExchangeConfig(state) {
      const _data = state.commodityDetails;

      if (!_data?.pointsExchangeInfo) return null;

      let dataVariants = _data?.variants ?? [];
      dataVariants = dataVariants.filter((it: AnyObject) => it.pointExchange);

      const _variants = dataVariants
        .map((it: AnyObject) => it.pointExchange)
        .sort((a: AnyObject, b: AnyObject) => {
          if (a?.points === b?.points) {
            return a?.cash - b?.cash;
          }
          return a?.points - b?.points;
        });

      const getMinMax = (arr: AnyObject[], key: string) => {
        if (!Array.isArray(arr) || !arr?.length) return { min: 0, max: 0 };
        return { min: arr?.[0]?.[key], max: arr?.[arr.length - 1]?.[key] };
      };

      let config = {
        // 用户侧 对商品的兑换数量是否已达限制
        isUserLimit: !state.exchangeableQuantity,
        // 所有规格是否已兑换完
        isAllSkuLimit: dataVariants?.every(
          (it: AnyObject) => it?.pointExchange?.exchangeTotal === 0
        ),
        // 是否积分兑换商品
        display: !!_data?.pointsExchangeInfo,
        // 积分值
        points: _data?.pointsExchangeInfo?.pointExchange?.points,
        // 积分区间
        integralRange: getMinMax(_variants, 'points'),
        // 价格
        cash: _data?.pointsExchangeInfo?.pointExchange?.cash,
        // 价格区间
        cashRange: getMinMax(_variants, 'cash'),
        // 是否允许购买
        allowPurchaseAtOriginalPrice: _data?.pointsExchangeInfo?.allowPurchaseAtOriginalPrice,
        // 积分兑换类型
        exchangeConditionType: _data?.pointsExchangeInfo?.exchangeConditionType,
        // 每人限兑数量
        exchangeLimit: _data?.pointsExchangeInfo?.exchangeLimit ?? 0,
        // unifiedExchangeRatio - 全积分兑换类型
        _variants,
      };

      console.log(`pointsExchangeConfig`, config);

      return config;
    },
    /** 订阅周期数据 */
    subscribePeriodAndDiscount(state): AnyObj[] {
      let list = state.commodityDetails?.customFields?.subscriptionPlan?.periodAndDiscount ?? [];

      if (!list.length) return list;

      const skuPrice = usePlaceOrderStore().pruchaseOptions.sku?.price ?? 0;
      const variantPrice = state.commodityDetails?.variants?.[0].price ?? 0;
      const unitPrice = !skuPrice ? variantPrice : skuPrice;

      list = list.map((it: AnyObj) => {
        return {
          ...it,
          _price: NP.times(unitPrice, NP.divide(NP.minus(100, it?.discountAmount ?? 0), 100)),
        };
      });

      return list;
    },
    /** 发货间隔数据 */
    subscriptionInterval(state) {
      const subscriptionInterval =
        state.commodityDetails?.customFields?.subscriptionPlan?.subscriptionInterval;

      if (subscriptionInterval) {
        subscriptionInterval._unit = businessUtils.order.getUnit(
          state.commodityDetails?.customFields?.subscriptionPlan?.subscriptionInterval?.unit
        );
      }
      return subscriptionInterval;
    },
    /** 订阅周期的单位 */
    subscribeUnit(state) {
      return state.commodityDetails?.customFields?.unit;
    },
  },

  actions: {
    async getProductDetails(id: string, refresh?: boolean) {
      if (!this.commodityDetails || refresh) {
        this.commodityDetails = await fetchProductDetails(id);

        if (this.commodityDetails?.customFields.subscriptionPlan) {
          this.isSubscribe = true;
        }
      }
      console.log(`商品详情数据 => `, this.commodityDetails);

      return this.commodityDetails;
    },
    async getActivityCountdownByProduct(id: string, refresh?: boolean) {
      if (!this.activityCountdownByProductData || refresh) {
        this.activityCountdownByProductData = await fetchActivityCountdownByProduct(id);
      }
      console.log(`商品活动数据 => `, this.activityCountdownByProductData);

      return this.activityCountdownByProductData;
    },
    async getExchangeableQuantity(productId: string, type: string, isIncludeCurrentOrder: boolean) {
      try {
        const res = await fetchGetExchangeableQuantity(productId, type, isIncludeCurrentOrder);
        this.exchangeableQuantity = res;
        console.log(`用户侧对积分商品剩余可兑换数量：`, res);
        return res;
      } catch (error) {
        console.log(error);
      }
    },
    reset() {
      this.commodityDetails = null;

      this.purchaseType = PlaceOrderType.regularOrder;

      const placeOrderStore = usePlaceOrderStore();

      placeOrderStore.pruchaseOptions = {
        sku: null,
        count: 1,
      };
    },
  },
});
